import { createContext, useContext, type ReactNode } from 'react';

/**
 * Which shell a shared feature component is currently mounted in.
 *
 * ── THE PROBLEM THIS SOLVES ──────────────────────────────────────────────────────────
 *
 * EventWizard and EventManage are ONE implementation used by two shells. Every link they
 * render — exit, back to the list, "finish and manage", the draft id the wizard pushes
 * into the URL — used to be written as a literal '/organizer/...'. An admin who opened
 * the wizard from the admin console was therefore thrown out of the admin route tree by
 * the first navigation the wizard made, React Router unmounted the admin shell, and the
 * sidebar vanished. That is the whole of the "Admin jumps into another portal" bug.
 *
 * The fix is NOT `isAdmin ? '/admin' : '/organizer'` scattered through the components,
 * which makes every component responsible for knowing about every shell. A component asks
 * where it is mounted, and the shell answers. Add a third shell later and the shared
 * components do not change.
 *
 * DEFAULT IS ORGANIZER ON PURPOSE. Nothing outside the admin tree has to be wrapped, and
 * an unwrapped component keeps exactly today's behaviour.
 */
export type RouteBase = '/admin' | '/organizer';

const RouteBaseContext = createContext<RouteBase>('/organizer');

export function RouteBaseProvider({ base, children }: { base: RouteBase; children: ReactNode }) {
  return <RouteBaseContext.Provider value={base}>{children}</RouteBaseContext.Provider>;
}

/** The path prefix of the shell this component is rendering inside. */
export function useRouteBase(): RouteBase {
  return useContext(RouteBaseContext);
}

/**
 * Event paths for a given shell.
 *
 * Pure, so a test can assert both shells' answers without rendering anything, and so the
 * shell decision lives in exactly one file.
 */
export function eventPaths(base: RouteBase) {
  return {
    /** Where "back to the list" goes. Admin has a real list; organizer's list is its index. */
    list: base === '/admin' ? '/admin/events' : '/organizer',
    create: `${base}/events/new`,
    draft: (eventId: string) => `${base}/events/new/${eventId}`,
    manage: (eventId: string) => `${base}/events/${eventId}`,
    lineup: (eventId: string) => `${base}/events/${eventId}/lineup`,
  };
}

/** The same paths, resolved for wherever the calling component is mounted. */
export function useEventPaths() {
  return eventPaths(useRouteBase());
}

/**
 * Single-entry helper for callers that already know the shell, per the task's
 * `eventPath({ mode, eventId })` shape.
 */
export function eventPath({
  mode, eventId, view = 'manage',
}: {
  mode: 'admin' | 'organizer';
  eventId?: string;
  view?: 'list' | 'create' | 'draft' | 'manage' | 'lineup';
}): string {
  const paths = eventPaths(mode === 'admin' ? '/admin' : '/organizer');
  if (view === 'list') return paths.list;
  if (view === 'create') return paths.create;
  if (!eventId) throw new Error(`eventPath: view "${view}" requires an eventId.`);
  if (view === 'draft') return paths.draft(eventId);
  if (view === 'lineup') return paths.lineup(eventId);
  return paths.manage(eventId);
}
